import { Button } from '@/client/components/ui/button';
import { cn } from '@/client/lib/utils';
import type { RouterOutputs } from '@/client/utils/trpc';

type AdminWeekStatusRow = RouterOutputs['timesheets']['adminWeekStatus'][number];

interface AdminTeamSidebarProps {
  employees: AdminWeekStatusRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onExport: () => void;
  isLoading: boolean;
}

export function AdminTeamSidebar({
  employees,
  selectedId,
  onSelect,
  onExport,
  isLoading,
}: AdminTeamSidebarProps) {
  return (
    <div className="flex w-64 shrink-0 flex-col border-r">
      <div className="border-b px-4 py-3">
        <p className="text-sm font-semibold">Team</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading && <p className="px-4 py-6 text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && employees.length === 0 && (
          <p className="px-4 py-6 text-sm text-muted-foreground">No team members.</p>
        )}
        {employees.map((emp) => (
          <button
            key={emp.id}
            type="button"
            onClick={() => onSelect(emp.id)}
            className={cn(
              'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/50',
              selectedId === emp.id && 'bg-secondary',
            )}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
              {(emp.full_name ?? '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{emp.full_name ?? '—'}</p>
              {emp.job_title && (
                <p className="truncate text-xs text-muted-foreground">{emp.job_title}</p>
              )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span className="tabular-nums text-xs text-muted-foreground">
                {emp.total_hours.toFixed(1)}h
              </span>
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                  emp.submitted
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
                )}
              >
                {emp.submitted ? 'Submitted' : 'Pending'}
              </span>
            </div>
          </button>
        ))}
      </div>
      <div className="border-t p-3">
        <Button variant="outline" size="sm" className="w-full" onClick={onExport}>
          Export XLSX
        </Button>
      </div>
    </div>
  );
}
